"use client";

import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangleIcon, CheckCircle2Icon } from "lucide-react";
import {
  useAiProvider,
  type AiProviderInfo,
} from "@/components/custom/contexts/aiProviderContext";
import { ProviderIcon } from "@/components/custom/providerIcons";
import { cn } from "@/lib/utils";
import { SettingsPageHeader } from "./settings-page-header";

export function SettingsAiContent() {
  const { t } = useTranslation();
  const { allProviders, selectedProvider, setProvider } = useAiProvider();

  const activeProvider =
    allProviders.find((p) =>
      selectedProvider ? p.name === selectedProvider : p.isDefault,
    ) ?? allProviders[0];

  const effectiveProvider = activeProvider?.name ?? null;

  const handleProviderSelect = useCallback(
    (name: string) => {
      setProvider(name);
    },
    [setProvider],
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto px-8 pt-8 pb-4">
        <div className="mx-auto max-w-[880px]">
          <SettingsPageHeader
            navKey="settings.nav.general"
            titleKey="settings.ai.title"
            subtitleKey="settings.ai.subtitle"
          />

          {/* Poskytovatel section */}
          <section className="mt-8">
            <div className="flex items-baseline gap-3 border-border/40 border-b pb-2">
              <h2 className="font-semibold text-[15px]">
                {t("settings.ai.providerSection")}
              </h2>
              <span className="text-[12.5px] text-muted-foreground">
                {t("settings.ai.providerHelper")}
              </span>
            </div>

            {/* Provider row */}
            <div className="mt-6 grid grid-cols-[280px_1fr] gap-6 border-border/50 border-b pb-6">
              <div>
                <span className="font-medium text-[13.5px]">
                  {t("settings.ai.activeProvider")}
                </span>
                <p className="mt-1 text-[12.5px] text-muted-foreground leading-relaxed">
                  {t("settings.ai.activeProviderDesc")}
                </p>
              </div>
              <div>
                <div className="grid grid-cols-2 gap-3">
                  {allProviders.map((p) => (
                    <ProviderCard
                      key={p.name}
                      provider={p}
                      isSelected={effectiveProvider === p.name}
                      onSelect={handleProviderSelect}
                    />
                  ))}
                </div>
                <p className="mt-3 text-[11.5px] text-muted-foreground">
                  {t("settings.ai.encryptionNote")}
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function ProviderCard({
  provider,
  isSelected,
  onSelect,
}: {
  provider: AiProviderInfo;
  isSelected: boolean;
  onSelect: (name: string) => void;
}) {
  const { t } = useTranslation();

  const modelSub = provider.modelId;

  return (
    <button
      type="button"
      onClick={() => onSelect(provider.name)}
      className={cn(
        "relative flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-left transition-colors",
        isSelected
          ? "border-foreground ring-1 ring-foreground/20"
          : "border-border hover:bg-muted/50",
      )}
    >
      <div
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-md",
          provider.type === "anthropic"
            ? "border border-[oklch(0.92_0.06_30)] bg-[oklch(0.96_0.04_30)]"
            : "border border-[oklch(0.92_0.06_150)] bg-[oklch(0.96_0.04_150)]",
        )}
      >
        <ProviderIcon provider={provider} className="size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-[13.5px]">
            {provider.displayName ?? provider.name}
          </span>
          {isSelected && (
            <span className="rounded-sm bg-muted px-1.5 py-0.5 font-medium font-mono text-[10px] text-muted-foreground">
              {t("settings.ai.currentPill")}
            </span>
          )}
        </div>
        <p className="mt-0.5 text-[11.5px] text-muted-foreground">
          {provider.isSharedKey
            ? `${modelSub} · ${t("settings.ai.keyNotSet")}`
            : modelSub}
        </p>
      </div>
      <div className="absolute top-3 right-3">
        {isSelected ? (
          <CheckCircle2Icon className="size-4 text-foreground" />
        ) : provider.isSharedKey ? (
          <AlertTriangleIcon className="size-4 text-amber-500" />
        ) : null}
      </div>
    </button>
  );
}
